import React, { useEffect, useMemo, useRef, useState } from 'react';

import RouteIcon from '@mui/icons-material/Route';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getOrdersInRoute } from './services';

import useRefreshOrder from '../../hooks/useRefreshOrders';

import {
  setOrdersModalConfig,
  setOrdersToDeliverFilters,
  setToDeliverCounter,
  setOrderQR,
  setConfirmationAnalytics,
} from './orderSlice';

import FilterSelect from '../common/_inputs/FilterSelect';
import OrderCard from './OrderCard';
import CustomizedDialogs from './OrderModalQR';
import ModalScanner from './ModalScanner';
import { ORDER_TYPES, SERVICES } from '@wareclouds/frontend-common/b2c';
import { ORDER_TYPE_PICKUP_AT_WARECLOUD } from '../../config/consts/orders';
import { useTranslation } from 'react-i18next';
import { SmallSpinner } from '../common/';
import {
  CONTEXT,
  CONTEXTS_WITH_POSTAL_CODE,
} from '../../config/consts/commons';
import { getOrderRecipientDisplayName } from './utils';
import { useIsRegionalHubClouder } from '../../hooks';

const OrdersToDeliver = () => {
  const { isRegionalHubClouder } = useIsRegionalHubClouder();
  const {
    cities,
    ordersToDeliverFilters: filters,
    ordersToDeliverOrderIndex: index,
  } = useSelector((state) => state.orders);

  const loadMoreBottomRef = useRef(true);
  const loadMoreTopRef = useRef(true);

  const scrollRef = useRef(null);

  const [orders, setOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [scanerOpen, setScanerOpen] = useState(false);
  const [orderSelect, setOrderSelect] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['description', 'glossary', 'button']);

  const [
    {
      isLoading,
      orders: ordersToDeliver,
      count,
      orderedByOptimizedRoute,
      canReOptimizedRoute,
    },
  ] = useRefreshOrder({
    apiCall: getOrdersInRoute,
    filters,
  });

  const hasExpressStorePickupOrders = useMemo(() => {
    return ordersToDeliver.some(
      (order) => order.id_order_type === ORDER_TYPES.EXPRESS
    );
  }, [ordersToDeliver]);

  useEffect(() => {
    dispatch(setToDeliverCounter(count));
  }, [dispatch, count]);

  useEffect(() => {
    const updateOrders = async () => {
      const ordersCopy = [...ordersToDeliver];

      if (!loadMoreTopRef.current) {
        ordersCopy[14].ref = scrollRef;
        await setOrders(ordersCopy);

        scrollRef.current.scrollIntoView();
        loadMoreTopRef.current = true;
      } else if (!loadMoreBottomRef.current) {
        ordersCopy[14].ref = scrollRef;
        await setOrders(ordersCopy);

        scrollRef.current.scrollIntoView();
        loadMoreBottomRef.current = true;
      } else {
        await setOrders(ordersCopy);
      }

      setOrdersLoading(false);
      setIsLoadingMore(false);
    };

    setOrdersLoading(true);
    if (!isLoading && ordersToDeliver && ordersToDeliver.length > 0) {
      updateOrders();
    } else if (!isLoading && ordersToDeliver && ordersToDeliver.length === 0) {
      setOrders([]);
      setOrdersLoading(false);
    }
  }, [ordersToDeliver, isLoading, index]);

  const toggleShowMoreInfo = (payload) => {
    const ordersCopy = [...orders];
    const selectedOrder = ordersCopy[payload];

    if (!selectedOrder) return;

    ordersCopy[payload] = {
      ...selectedOrder,
      showMore: !selectedOrder.showMore,
    };

    setOrders(ordersCopy);
  };

  const setPlaceholder = () => {
    const message =
      !filters.filter && !filters.id_city
        ? t('orders.deliver.no_orders', { ns: 'description' })
        : t('orders.deliver.no_search_orders', { ns: 'description' });

    return (
      <Card className="order-card text-center py-3">
        {ordersLoading ? (
          <SmallSpinner />
        ) : (
          <>
            <Row className="w-100">
              <SentimentDissatisfiedIcon
                sx={{ fontSize: 60 }}
                className="text-secondary w-100 mb-2"
              />
            </Row>
            <p className="m-auto">{message}</p>
          </>
        )}
      </Card>
    );
  };

  const handleRetrieveOrderPrompt = ({ order }) => {
    dispatch(
      setOrdersModalConfig({
        titleOpening: t('orders.deliver.modal.wish_pickup_order', {
          ns: 'description',
        }),
        show: true,
        order,
        modalType: 'toDeliver',
      })
    );
  };

  const handleReturnOrderPrompt = (payload) => {
    const isPickupType11 =
      payload.id_order_type === ORDER_TYPE_PICKUP_AT_WARECLOUD;

    dispatch(
      setOrdersModalConfig({
        titleOpening: isPickupType11
          ? t('orders.deliver.modal.cannot_pickup_order', {
              ns: 'description',
              defaultValue: 'No puedes retirar el pedido',
            })
          : t('orders.deliver.modal.need_return_order', {
              ns: 'description',
            }),
        show: true,
        order: { ...payload },
        modalType: isPickupType11 ? 'toReportPickupType11' : 'toReport',
      })
    );
  };
  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const handleDeliverOrderPrompt = (payload) => {
    const { order, isNaturaOrder } = payload;

    if (order.id_order_type === ORDER_TYPE_PICKUP_AT_WARECLOUD) {
      dispatch(
        setOrdersModalConfig({
          titleOpening: t('orders.deliver.modal.wish_pickup_order', {
            ns: 'description',
          }),
          show: true,
          order,
          modalType: 'toDeliverType11',
        })
      );
      dispatch(setConfirmationAnalytics('pickup-type11-flow'));
      setOrderSelect(order);
      return;
    }

    const hasOnlyLastMileService =
      order.ecommerce_services?.length === 1 &&
      order.ecommerce_services[0].id_service === SERVICES.LAST_MILE;

    if (hasOnlyLastMileService || isNaturaOrder) {
      dispatch(
        setOrdersModalConfig({
          titleOpening: t('orders.deliver.modal.wish_deliver_order', {
            ns: 'description',
          }),
          show: true,
          order,
          modalType: 'toDeliver',
        })
      );
    } else {
      openDialog();
    }

    setOrderSelect(order);
  };
  const handleQR = () => {
    dispatch(setOrderQR(orderSelect));
    dispatch(setConfirmationAnalytics('QR-confirmation'));
    setScanerOpen(true);
    setIsDialogOpen(false);
  };

  const handleOrderDeliveryPrompt = () => {
    setIsDialogOpen(false);
    dispatch(
      setOrdersModalConfig({
        titleOpening: t('orders.deliver.modal.wish_deliver_order', {
          ns: 'description',
        }),
        show: true,
        order: { ...orderSelect },
        modalType: 'toDeliver',
      })
    );
  };

  return (
    <>
      <Row className="justify-content-between align-items-center">
        <Col xs="auto" className="p-0">
          <FilterSelect
            name="id_city"
            handleChange={(e) => {
              const { id_city, ...rest } = filters;
              if (e.target.value) {
                dispatch(
                  setOrdersToDeliverFilters({
                    id_city: parseInt(e.target.value, 10),
                    ...rest,
                  })
                );
              } else {
                dispatch(setOrdersToDeliverFilters({ id_city: '', ...rest }));
              }
            }}
            defaultLabel={t('city', { ns: 'glossary' })}
            currentValue={filters.id_city}
            payload={cities}
          />
        </Col>
        {!isRegionalHubClouder && (
          <Col xs={12} md="auto" className="p-0">
            <Button
              disabled={
                (!canReOptimizedRoute &&
                  (orderedByOptimizedRoute || orders.length < 1)) ||
                hasExpressStorePickupOrders
              }
              className="my-2 w-100"
              style={{ fontSize: '0.9em' }}
              onClick={() => navigate('/routes/optimized')}
            >
              <RouteIcon className="route-icon" />
              {canReOptimizedRoute
                ? t('re_optimize', { ns: 'button' })
                : t('optimize', { ns: 'button' })}
            </Button>
          </Col>
        )}
        {orders.length < 1 ? (
          setPlaceholder()
        ) : isRegionalHubClouder ? null : orderedByOptimizedRoute ? (
          <p className="text-center">
            {t('orders.deliver.after_optimize', { ns: 'description' })}
          </p>
        ) : (
          <p className="text-center">
            {t('orders.deliver.before_optimize', { ns: 'description' })}
          </p>
        )}
      </Row>
      <Row className="cards-container">
        {isLoadingMore && !loadMoreTopRef.current && <SmallSpinner />}

        {orders.map((order, index) => (
          <OrderCard
            view="toDeliver"
            orderedByOptimizedRoute={orderedByOptimizedRoute}
            key={`${index}-${order.order_code}-deliver`}
            order={order}
            index={index}
            toggleShowMoreInfo={toggleShowMoreInfo}
            buttonText={
              order.related_order
                ? t('withdraw_and_deliver', { ns: 'button' })
                : order.id_order_type === ORDER_TYPE_PICKUP_AT_WARECLOUD
                ? t('withdraw', { ns: 'button' })
                : t('deliver', { ns: 'button' })
            }
            cardUser={{
              label: 'Destinatario',
              value:
                getOrderRecipientDisplayName(order) ||
                order.warecloud_name ||
                '-',
            }}
            outerAttributes={['consumer_address', 'consumer_city']}
            innerAttributes={[
              'consumer_address_detail',
              ...(CONTEXTS_WITH_POSTAL_CODE.includes(CONTEXT)
                ? ['consumer_postal_code']
                : []),
              'consumer_address_extra_info',
              'consumer_phone',
              'consumer_email',
            ]}
            handlePrompt={
              !order.related_order
                ? handleDeliverOrderPrompt
                : handleRetrieveOrderPrompt
            }
            extra={
              <Col xs={12} className="my-2 text-center">
                <Button
                  variant="secondary"
                  onClick={() => handleReturnOrderPrompt(order)}
                >
                  {order.id_order_type === ORDER_TYPE_PICKUP_AT_WARECLOUD
                    ? t('not_withdraw', {
                        ns: 'button',
                        defaultValue: 'No pude retirar el pedido',
                      })
                    : t('not_deliver', { ns: 'button' })}{' '}
                  <SentimentDissatisfiedIcon />
                </Button>
              </Col>
            }
            isPending={false}
          />
        ))}

        {isLoadingMore && !loadMoreBottomRef.current && <SmallSpinner />}
      </Row>
      {isDialogOpen && (
        <CustomizedDialogs
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onConfirmDelivery={handleOrderDeliveryPrompt}
          selectedOrder={orderSelect}
          handleQR={handleQR}
        />
      )}
      <ModalScanner onClose={() => setScanerOpen(false)} open={scanerOpen} />
    </>
  );
};

export default OrdersToDeliver;
